import { LockKeyhole } from "lucide-react";
import { Link } from "react-router-dom";

import { offerKeyFacts } from "@sill/shared/components/marketplace/offer-card";

import { VERIFY_FINANCES_HREF } from "../../lib/offers-display";
import {
  useTierUnlockRow,
  type TierUnlockRowData,
} from "../../hooks/useTierUnlockRow";
import { tierOrdinal } from "./TierUnlockRow.helpers";

export interface TierUnlockRowProps {
  getToken: () => Promise<string | null>;
  /** The buyer's OWN tier string (e.g. "t1") — never t3 (callers must not
   *  enable the row there; see `useTierUnlockRow`'s module doc). */
  currentTier?: string;
  /** The first browse agent's `pro_user_id` — the ONE candidate the tier-
   *  above teaser is drawn from (design §4.1). */
  firstAgentProUserId: string | null;
  onUnlockClicked?: (currentTier: string, lockedTier: string) => void;
}

/**
 * Plan 435 M3 (mfh-tier-unlock-row). One locked ladder row under the agent
 * cards: a concrete, honest "what verifying unlocks" number instead of an
 * abstract score (tech-spec Concepts "Tier-above teaser"). Omitted when the
 * buyer is at T3, no locked entry exists, or the one extra request fails.
 */
export function TierUnlockRow({
  getToken,
  currentTier,
  firstAgentProUserId,
  onUnlockClicked,
}: TierUnlockRowProps) {
  const enabled = Boolean(firstAgentProUserId) && currentTier !== "t3";
  const { data } = useTierUnlockRow(getToken, enabled, firstAgentProUserId);

  return (
    <TierUnlockRowView
      currentTier={currentTier}
      data={data}
      onUnlockClicked={onUnlockClicked}
    />
  );
}

export interface TierUnlockRowViewProps {
  data: TierUnlockRowData | null;
  currentTier?: string;
  onUnlockClicked?: (currentTier: string, lockedTier: string) => void;
}

/**
 * Plan 435 M3 (mfh-preview-fixtures enabler). The pure, hook-free render —
 * split out so `/preview/home` can exercise the SAME visual with
 * hand-built fixture data and no network, rather than re-implementing the
 * row's markup a second time.
 */
export function TierUnlockRowView({
  data,
  currentTier,
  onUnlockClicked,
}: TierUnlockRowViewProps) {
  if (!data) return null;

  const { entry, agentDisplayName } = data;
  const facts = offerKeyFacts({
    commissionPct: entry.commission_pct,
    items: entry.items,
  });
  const tierNumber = tierOrdinal(entry.buyer_tier);
  const agentName = agentDisplayName ?? "This agent";
  const headline = `${agentName}'s Tier ${tierNumber} offer${
    facts.length > 0 ? `: ${facts.join(" · ")}` : ""
  }`;

  return (
    <section data-testid="home-tier-unlock">
      <Link
        className="marketplace-surface-interactive flex min-h-12 w-full items-start gap-3 px-4 py-3 text-left opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        data-testid="home-tier-unlock-link"
        onClick={() =>
          onUnlockClicked?.(currentTier ?? "", entry.buyer_tier)
        }
        to={VERIFY_FINANCES_HREF}
      >
        <LockKeyhole
          aria-label="Locked"
          className="mt-0.5 size-4 shrink-0"
        />
        {/* Code-review fix (M3 iteration 2, finding 4): the offer facts are
            the whole point of this row — never truncate them. `min-w-0`
            (not `shrink-0`) lets the span take the row's remaining width
            and wrap up to a few lines instead of forcing overflow or
            clipping, matching the Ladder locked-row chrome otherwise. */}
        <span
          className="min-w-0 flex-1 text-wrap-pretty text-sm font-semibold tabular-nums text-foreground"
          data-testid="home-tier-unlock-headline"
        >
          {headline}
        </span>
        <span className="mt-0.5 inline-flex shrink-0 items-center gap-1 text-xs font-bold text-primary">
          Verify to unlock
          <span aria-hidden>›</span>
        </span>
      </Link>
    </section>
  );
}
